import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { fmtDate } from "@/admin/adminLabels";
import { toast } from "sonner";

const STATUS = {
  new: { label: "Nuovo", bg: "#FEF3C7", color: "#0B192C" },
  read: { label: "Letto", bg: "#E2E8F0", color: "#0B192C" },
  replied: { label: "Risposto", bg: "#0B192C", color: "#FFFFFF" },
  archived: { label: "Archiviato", bg: "#F1F5F9", color: "#475569" },
};

export default function ContactMessagesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contact-messages");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact-messages/${id}`, { status });
      toast.success("Aggiornato");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="label-eyebrow">Messaggi modulo contatti</p>
          <h2 className="mt-2 font-serif text-2xl text-navy">Inbox /contatti</h2>
        </div>
        <p className="text-sm text-ink">{items.length} messaggi</p>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sand">
            <tr className="text-left">
              {["Mittente", "Oggetto", "Stato", "Data"].map((h) => (
                <th key={h} className="p-3 text-[11px] uppercase tracking-wider text-ink">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-8 text-center text-ink">Caricamento…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-ink">Nessun messaggio ricevuto.</td></tr>}
            {items.map((m) => {
              const st = STATUS[m.status] || STATUS.new;
              return (
                <tr
                  key={m.id}
                  data-testid={`msg-row-${m.id}`}
                  onClick={() => {
                    setSelected(m);
                    if (m.status === "new") updateStatus(m.id, "read");
                  }}
                  className="border-t border-[#E2E8F0] hover:bg-sand/40 cursor-pointer"
                >
                  <td className="p-3">
                    <p className="font-medium text-navy">{m.name}</p>
                    <p className="text-xs text-ink truncate max-w-[260px]">{m.email}</p>
                  </td>
                  <td className="p-3 text-navy">{m.subject || <span className="text-ink">(senza oggetto)</span>}</td>
                  <td className="p-3">
                    <span className="chip border-0 text-[11px]" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                  </td>
                  <td className="p-3 text-ink text-xs whitespace-nowrap">{fmtDate(m.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && (
          <DialogContent data-testid="msg-detail-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-navy">{selected.subject || "(senza oggetto)"}</DialogTitle>
              <p className="text-sm text-ink">Ricevuto {fmtDate(selected.created_at)}</p>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-[#E2E8F0] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-ink">Nome</p>
                  <p className="text-sm text-navy">{selected.name}</p>
                </div>
                <div className="rounded-lg border border-[#E2E8F0] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-ink">Email</p>
                  <a href={`mailto:${selected.email}`} className="text-sm text-navy underline-offset-4 hover:underline flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {selected.email}</a>
                </div>
                <div className="rounded-lg border border-[#E2E8F0] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-ink">Telefono / WA</p>
                  <p className="text-sm text-navy flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {selected.phone || "—"}</p>
                </div>
              </div>

              <div className="rounded-xl border border-[#E2E8F0] p-4 bg-sand/30">
                <p className="text-[11px] uppercase tracking-wider text-ink">Messaggio</p>
                <p className="mt-2 text-sm text-navy whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>

              <div className="flex items-end justify-between gap-3">
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-wider text-ink mb-1">Stato</p>
                  <Select value={selected.status || "new"} onValueChange={(v) => { updateStatus(selected.id, v); setSelected({ ...selected, status: v }); }}>
                    <SelectTrigger data-testid="msg-status" className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline" className="border-navy/20 text-navy hover:bg-sand">
                    <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Richiesta Seamatch")}`}>
                      <Mail className="h-4 w-4 mr-1.5" /> Rispondi via email
                    </a>
                  </Button>
                  {selected.phone && (
                    <Button asChild className="bg-[#25D366] hover:bg-[#1eb558] text-white">
                      <a href={`https://wa.me/${selected.phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer">
                        <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
